import { create } from "zustand";
import { useAuthStore } from "./auth";

// ── Entity events (existing) ─────────────────────────────

export interface WsEvent {
	action: string;
	entity_type: string;
	entity_id: string;
	workspace_id?: string;
	actor?: string;
}

// ── KV updates (new) ─────────────────────────────────────

export interface KvUpdate {
	bucket: string;
	key: string;
	workspace_id: string;
	value: Record<string, unknown>;
	revision: number;
}

// ── Envelope parsing ─────────────────────────────────────

interface NatsEnvelope {
	subject: string;
	workspace_id: string;
	entity_id: string;
	payload: Record<string, unknown>;
}

function parseEnvelope(raw: string): {
	entity?: WsEvent;
	kv?: KvUpdate;
} | null {
	try {
		const data = JSON.parse(raw);

		// KV update: { type: "kv", bucket, key, workspace_id, value, revision }
		if (data.type === "kv" && data.bucket && data.key) {
			return {
				kv: {
					bucket: data.bucket,
					key: data.key,
					workspace_id: data.workspace_id ?? "",
					value: data.value ?? {},
					revision: data.revision ?? 0,
				},
			};
		}

		// Standard entity envelope: omnios.entity.{kind}.{event_type}
		if (data.subject && data.entity_id) {
			const env = data as NatsEnvelope;
			const parts = env.subject.split(".");

			if (
				parts.length >= 4 &&
				parts[0] === "omnios" &&
				parts[1] === "entity"
			) {
				const entityKind = parts[2];
				const eventType = parts[3];
				return {
					entity: {
						action: `${entityKind}.${eventType}`,
						entity_type: entityKind,
						entity_id: env.entity_id,
						workspace_id: env.workspace_id,
						actor: env.payload?.actor as string | undefined,
					},
				};
			}
		}

		return null;
	} catch {
		return null;
	}
}

// ── Zustand store ────────────────────────────────────────

interface WsState {
	status: "idle" | "connecting" | "open" | "closed";
	connect: () => void;
	disconnect: () => void;

	// Entity event listeners (existing)
	listeners: Set<(evt: WsEvent) => void>;
	subscribe: (cb: (evt: WsEvent) => void) => () => void;

	// KV update listeners (new)
	kvListeners: Set<(update: KvUpdate) => void>;
	subscribeKv: (cb: (update: KvUpdate) => void) => () => void;

	// KV state cache (latest value per bucket:key)
	kvCache: Map<string, KvUpdate>;
}

let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelay = 1000;
const MAX_DELAY = 30000;

export const useWsStore = create<WsState>()((set, get) => ({
	status: "idle",
	listeners: new Set(),
	kvListeners: new Set(),
	kvCache: new Map(),

	connect: () => {
		if (ws && ws.readyState !== WebSocket.CLOSED) {
			return;
		}

		const token = useAuthStore.getState().token;
		if (!token) return;

		const wsUrl = import.meta.env.VITE_WS_URL as string | undefined;
		const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
		let url: string;
		if (wsUrl) {
			url = `${wsUrl}/ws?token=${token}`;
		} else if (apiUrl) {
			const base = apiUrl.replace(/^http/, "ws").replace(/:\d+$/, ":50081");
			url = `${base}/ws?token=${token}`;
		} else {
			const proto = window.location.protocol === "https:" ? "wss" : "ws";
			const host = window.location.host;
			url = `${proto}://${host}/ws?token=${token}`;
		}

		set({ status: "connecting" });
		ws = new WebSocket(url);

		ws.onopen = () => {
			set({ status: "open" });
			reconnectDelay = 1000;
		};

		ws.onmessage = (msg) => {
			const parsed = parseEnvelope(msg.data as string);
			if (!parsed) return;

			// Entity event
			if (parsed.entity) {
				for (const cb of get().listeners) {
					cb(parsed.entity);
				}
			}

			// KV update
			if (parsed.kv) {
				const update = parsed.kv;
				const cacheKey = `${update.bucket}:${update.key}`;

				// Update cache
				const cache = get().kvCache;
				const existing = cache.get(cacheKey);
				if (!existing || existing.revision < update.revision) {
					cache.set(cacheKey, update);
				}

				// Notify listeners
				for (const cb of get().kvListeners) {
					cb(update);
				}
			}
		};

		ws.onclose = () => {
			set({ status: "closed" });
			ws = null;
			reconnectTimer = setTimeout(() => {
				get().connect();
				reconnectDelay = Math.min(reconnectDelay * 2, MAX_DELAY);
			}, reconnectDelay);
		};

		ws.onerror = () => {
			ws?.close();
		};
	},

	disconnect: () => {
		if (reconnectTimer) {
			clearTimeout(reconnectTimer);
			reconnectTimer = null;
		}
		ws?.close();
		ws = null;
		set({ status: "idle" });
	},

	subscribe: (cb) => {
		get().listeners.add(cb);
		return () => {
			get().listeners.delete(cb);
		};
	},

	subscribeKv: (cb) => {
		get().kvListeners.add(cb);
		return () => {
			get().kvListeners.delete(cb);
		};
	},
}));

export function sendPresence(page: string) {
	if (ws?.readyState === WebSocket.OPEN) {
		ws.send(
			JSON.stringify({
				type: "presence",
				page,
			}),
		);
	}
}
